import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * SWUpdatePrompt — nudges returning walkers when a new service worker is
 * waiting to activate. Shows a persistent sonner toast ("Update ready —
 * tap to refresh") that posts SKIP_WAITING to the waiting worker and then
 * reloads the page on `controllerchange`.
 *
 * Only runs in production (dev uses hot reload, no SW registered).
 *
 * The toast is dismissible AND de-duplicated per-session so a walker who
 * dismisses it isn't pestered on every route change. A fresh SW install
 * will show it again in a new session.
 */
const DISMISSED_KEY = "td_sw_update_dismissed";
const TOAST_ID = "sw-update-ready";

export default function SWUpdatePrompt() {
  const shownRef = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!("serviceWorker" in navigator)) return;

    let reloading = false;
    const onControllerChange = () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    const promptForWaiting = (waiting) => {
      if (!waiting || shownRef.current) return;
      try {
        if (sessionStorage.getItem(DISMISSED_KEY) === "1") return;
      } catch { /* ignore */ }
      // Never pop the update toast on top of the What's new modal — the
      // modal is itself a release-note surface, and layering a "Update
      // ready" nudge on top of it is noisy. Wait for the modal to
      // dismiss (polled lightly) and prompt once it's gone.
      const whatsNewOpen = () =>
        !!document.querySelector('[data-testid="whats-new-modal"]');
      if (whatsNewOpen()) {
        const poll = setInterval(() => {
          if (!whatsNewOpen()) {
            clearInterval(poll);
            promptForWaiting(waiting);
          }
        }, 500);
        return;
      }
      shownRef.current = true;
      toast.custom(
        (t) => (
          <div
            data-testid="sw-update-toast"
            className="flex items-center gap-3 bg-brand-sand text-brand-text rounded-full border border-brand-border shadow-lg pl-4 pr-1 py-1"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            <span className="text-sm font-medium">Update ready — tap to refresh</span>
            <button
              onClick={() => waiting.postMessage("SKIP_WAITING")}
              className="rounded-full bg-brand-primary text-brand-sand font-semibold text-sm px-4 py-1.5 hover:brightness-110 transition-[filter]"
              data-testid="sw-update-refresh"
            >
              Refresh
            </button>
            <button
              onClick={() => {
                try { sessionStorage.setItem(DISMISSED_KEY, "1"); } catch { /* ignore */ }
                toast.dismiss(t);
              }}
              className="w-7 h-7 rounded-full text-brand-subtle hover:text-brand-text flex items-center justify-center"
              aria-label="Dismiss update prompt"
              data-testid="sw-update-dismiss"
            >
              ×
            </button>
          </div>
        ),
        { id: TOAST_ID, duration: Infinity, position: "top-center" },
      );
    };

    const wireRegistration = (reg) => {
      if (!reg) return;
      // Case A: a waiting worker is already parked from a previous visit.
      if (reg.waiting && navigator.serviceWorker.controller) {
        promptForWaiting(reg.waiting);
      }
      // Case B: a new worker starts installing while this tab is open.
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) {
            promptForWaiting(reg.waiting || nw);
          }
        });
      });
    };

    // Register (or re-use) the SW and attach the listeners.
    const register = async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        wireRegistration(reg);
        // Nudge the browser to check for a new SW version — useful after
        // a redeploy where the HTML was cached but the SW file is fresh.
        try { reg.update(); } catch { /* ignore */ }
      } catch (err) {
        console.warn("SW registration failed", err);
      }
    };
    // Defer to `load` so we don't compete with first-paint work.
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });

    // Re-check for a waiting worker when the tab comes back to the foreground.
    const onVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          try { reg.update(); } catch { /* ignore */ }
          if (reg.waiting && navigator.serviceWorker.controller) {
            promptForWaiting(reg.waiting);
          }
        }
      } catch { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
